// src/components/tarefas/DescricaoEditor.tsx
// ✅ Editor avançado para descrições com checkboxes, bullets e numeração + MENÇÕES CORRIGIDAS + ALTURA DINÂMICA

import { CheckSquare, List, Loader2, Paperclip,Send, Type, User } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// 🔁 lazy memo do mentionsService
let __mentionsMod: any;
async function mentions() {
  if (!__mentionsMod) __mentionsMod = await import('@/features/comunicacao/services/mentionsService');
  return __mentionsMod;
}
import { normalizeAllToGFM } from "@/features/comunicacao/utils/taskList";
import { useDraft } from '@/features/sistema/hooks/useDraft';
import { supabase } from '@/features/sistema/services/supabaseClient';

// ✅ CORREÇÃO 1: Interface para Usuario
interface Usuario {
  id: string;
  nome: string;
  email: string;
}

async function loadUsuariosMencao(): Promise<Usuario[]> {
  // Tenta a RPC que criamos (funciona em qualquer ambiente)
  const { data, error } = await supabase.rpc('fn_app_usuarios_mencao_list');
  if (error) {
    console.warn('[MENCAO] fallback RPC falhou, tentando /usuarios:', error);
    // fallback para bases antigas
    const r = await supabase
      .from('usuarios')
      .select('id,nome,email,role,funcao')
      .eq('status', 'ativo')
      .neq('role', 'cliente')
      .neq('funcao', 'cliente')
      .order('nome', { ascending: true });
    if (r.status === 404) return [];
    if (r.error) {
      console.error('[MENCAO] erro completo:', r.error);
      return [];
    }
    return (r.data ?? []) as Usuario[];
  }
  return (data ?? []) as Usuario[];
}

// ---- Fail-safe: abre o file picker fora de overlays/overflow (útil no Android) ----
function openFilePickerViaPortal(opts: { accept: string; multiple: boolean; onFiles: (files: File[]) => void }) {
  if (typeof document === 'undefined') return;

  const { accept, multiple, onFiles } = opts;
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = accept;
  input.multiple = multiple;
  input.style.position = 'fixed';
  input.style.left = '-9999px';
  input.style.top = '0';
  input.style.opacity = '0';
  document.body.appendChild(input);

  input.onchange = () => {
    const files = Array.from(input.files || []);
    try {
      onFiles(files);
    } finally {
      input.remove();
    }
  };

  // segurança extra para Android: dispara no próximo tick
  setTimeout(() => input.click(), 0);
}

// --- MODULE-LEVEL: PDF thumbnail support ---
let __pdfWorkerReady = false;
async function ensurePdfWorker() {
  if (__pdfWorkerReady) return;
  // @ts-ignore - Vite resolve para URL do worker
  const workerSrc = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
  const pdf = await import('pdfjs-dist');
  // @ts-ignore
  pdf.GlobalWorkerOptions.workerSrc = workerSrc;
  __pdfWorkerReady = true;
}

function PdfThumb({ src, className = "" }: { src: string | File; className?: string }) {
  const [thumb, setThumb] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await ensurePdfWorker();
        const pdfMod: any = await import('pdfjs-dist');
        let loadingTask: any;
        if (typeof src === 'string') {
          loadingTask = pdfMod.getDocument(src);
        } else {
          const buf = await src.arrayBuffer();
          loadingTask = pdfMod.getDocument({ data: buf });
        }
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1 });
        const targetW = 220;
        const scale = Math.max(0.2, targetW / viewport.width);
        const vp = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('canvas ctx null');
        canvas.width = Math.max(1, Math.floor(vp.width));
        canvas.height = Math.max(1, Math.floor(vp.height));
        await page.render({ canvasContext: ctx, viewport: vp }).promise;
        const url = canvas.toDataURL('image/png');
        if (!cancelled) setThumb(url);
      } catch (e) {
        if (!cancelled) setThumb(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [src]);

  if (thumb) {
    return <img src={thumb} className={className} alt="PDF thumbnail" />;
  }
  return (
    <div className={`w-full h-16 bg-secondary/30 dark:bg-gray-800 flex items-center justify-center text-[11px] font-medium ${className}`}>
      {loading ? 'Carregando…' : 'PDF'}
    </div>
  );
}

// --- MODULE-LEVEL: Debug helper ---
const dbg = (label: string, files: File[]) =>
  console.log(label, files.map(f => ({ name: f.name, size: f.size, type: f.type })));

interface DescricaoEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  usuarios?: Usuario[]; // ✅ NOVO: Lista de usuários para menções
  minRows?: number; // ✅ NOVO: Número mínimo de linhas
  maxRows?: number; // ✅ NOVO: Número máximo de linhas
  onEnsureVisible?: (el: HTMLTextAreaElement) => void; // 🔧 NOVO: garantir caret visível no mobile
  /** Quando o usuário cola imagens/vídeos (clipboard) */
  onPasteFiles?: (files: File[]) => void;
  /** Enviar com Ctrl/Cmd + Enter */
  submitOnCtrlEnter?: boolean;
  /** Enviar com Enter (útil no mobile) */
  submitOnEnter?: boolean;
  /** Callback disparado ao enviar (comentários) */
  onSubmit?: () => void;
  /** (Opcional) Envio com payload estruturado — útil p/ telas que querem tratar tudo num lugar */
  onSubmitWithPayload?: (payload: { text: string; mentions: string[]; files: File[] }) => void;
  /** Mostrar barra de formatação */
  showToolbar?: boolean;
  /** Focar automaticamente o campo */
  autoFocus?: boolean;
  /** Arquivos pendentes para upload */
  pendingFiles?: File[];
  /** Mostrar pré-visualização dos anexos pendentes */
  showPendingPreview?: boolean;
  /** Remover um arquivo pendente (usado nos thumbs do editor) */
  onRemovePendingFile?: (file: File) => void;
  /** (Opcional) Estado externo de envio — desabilita botão e mostra spinner */
  sending?: boolean;
  /** (Opcional) Rótulo do botão Enviar */
  sendLabel?: string;
  /** ✅ NOVO: Habilitar salvamento local automático */
  enableLocalSave?: boolean;
  /** ✅ NOVO: Chave única para identificar o rascunho (obrigatória se enableLocalSave=true) */
  draftKey?: string;
  /** ✅ NOVO: TTL do rascunho em horas (padrão: 168h = 7 dias) */
  draftTTL?: number;
  /** ✅ NOVO: Força abertura do picker via portal (melhor suporte mobile) */
  usePortalPicker?: boolean;
}

const EMPTY_FILE_LIST: File[] = [];

export default function DescricaoEditor({
  value,
  onChange,
  placeholder = "Escreva um comentário…",
  disabled = false,
  className = "",
  usuarios = [],
  minRows = 3, // ✅ NOVO: Mínimo de 3 linhas
  maxRows = 15, // ✅ NOVO: Máximo de 15 linhas
  onEnsureVisible,
  onPasteFiles,
  submitOnCtrlEnter = true,
  submitOnEnter = false,
  onSubmit,
  onSubmitWithPayload,
  showToolbar = true,
  autoFocus = false,
  pendingFiles = EMPTY_FILE_LIST,
  showPendingPreview = false,
  onRemovePendingFile,
  sending = false,
  sendLabel = 'Enviar',
  enableLocalSave = false, // ✅ NOVO: Desabilitado por padrão para manter compatibilidade
  draftKey,
  draftTTL = 168, // ✅ NOVO: 7 dias por padrão
  usePortalPicker = false,
}: DescricaoEditorProps) {
  // ✅ CORREÇÃO: Garantir que value seja sempre uma string válida
  const safeValue = value || '';
  
  // ✅ NOVO: Salvamento local opcional usando useDraft
  type EditorDraft = { text: string };
  const draftConfig = enableLocalSave && draftKey ? {
    key: draftKey,
    initial: { text: "" },
    ttlHours: draftTTL
  } : null;
  
  const draftHook = useDraft<EditorDraft>(draftConfig || { key: null, initial: { text: "" } });
  const { value: draftValue, setValue: setDraftValue, loadDraft, scheduleSave, clearDraft } = draftHook;
  
  // Se o salvamento local estiver habilitado, use o valor do draft; caso contrário, use o valor externo
  const effectiveValue = enableLocalSave && draftKey ? draftValue.text : safeValue;
  const setEffectiveValue = useMemo(() =>
    enableLocalSave && draftKey
      ? (text: string) => {
          setDraftValue({ text });
          scheduleSave({ text });
          onChange(text); // Sempre sincroniza com o componente pai
        }
      : onChange,
    [enableLocalSave, draftKey, onChange, scheduleSave, setDraftValue]
  );
  
  const [dragOver, setDragOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
   const containerRef = useRef<HTMLDivElement>(null);
   const fileInputRef = useRef<HTMLInputElement>(null);
   const toolbarRef = useRef<HTMLDivElement>(null);
  // URLs locais para pré-visualização dos anexos pendentes
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});

  // Flags e acceptStr para iOS + Android + Desktop
  const [fileInputKey, setFileInputKey] = useState(0);


  // Agora: imagens + vídeos + PDF (iOS/Android podem limitar a seleção nativamente)
  const acceptStr = 'image/*,video/*,application/pdf';

  // --- Helpers de dedupe centralizado no Editor ---
  const fileSig = (f: File) => `${f.name}|${f.size}|${(f as any).lastModified ?? 0}`;
  const normalizedPendingFiles = pendingFiles ?? EMPTY_FILE_LIST;

  const uniqueNewFiles = useCallback((files: File[]): File[] => {
    const existing = new Set((normalizedPendingFiles ?? []).map(fileSig));
    const batch = new Set<string>();
    const out: File[] = [];
    for (const f of files) {
      const sig = fileSig(f);
      if (!existing.has(sig) && !batch.has(sig)) {
        batch.add(sig);
        out.push(f);
      }
    }
    return out;
  }, [normalizedPendingFiles]);

  const handleAttachClick = useCallback(() => {
    if (disabled || sending) return;

    if (usePortalPicker) {
      openFilePickerViaPortal({
        accept: acceptStr,
        multiple: true,
        onFiles: (files) => {
          dbg('[PortalPicker]', files);
          const unicos = uniqueNewFiles(files);
          if (unicos.length) onPasteFiles?.(unicos);
        }
      });
      return;
    }

    fileInputRef.current?.click();
  }, [disabled, sending, usePortalPicker, acceptStr, uniqueNewFiles, onPasteFiles]);

  // --- Menções ---
  const [mostrarSugestoes, setMostrarSugestoes] = useState(false);
  const [sugestoes, setSugestoes] = useState<Usuario[]>([]);
  const [carregandoSugestoes, setCarregandoSugestoes] = useState(false);
  const [posicaoMencao, setPosicaoMencao] = useState<{ inicio: number; termo: string } | null>(null);
  const [indiceSelecionado, setIndiceSelecionado] = useState(0);
  const [posicaoDropdown, setPosicaoDropdown] = useState({ top: 0, left: 0 });
  const sugestoesRef = useRef<HTMLDivElement>(null);

  // controle mínimo de debounce/cancelamento
  const mencaoReqRef = useRef(0);
  function debounceBuscar(fn: () => void, ms = 180) {
    const id = ++mencaoReqRef.current;
    setTimeout(() => { if (id === mencaoReqRef.current) fn(); }, ms);
  }

  // ✅ CORREÇÃO 2: Função para buscar usuários (local ou remoto)
  const buscarSugestoes = (termo: string) => {
    setCarregandoSugestoes(true);
    
    try {
      // Se a página fornecer usuarios, filtra localmente
      if (Array.isArray(usuarios) && usuarios.length > 0) {
        const lista = (!termo ? usuarios.slice(0, 8)
                              : usuarios.filter(u => u.nome.toLowerCase().includes(termo.toLowerCase())).slice(0, 8));
        setSugestoes(lista);
        setIndiceSelecionado(0);
        setPosicaoDropdown(calcularPosicaoDropdown());
        setCarregandoSugestoes(false);
        return;
      }
      
      // Caso contrário, faz fallback usando loadUsuariosMencao (RPC com fallback)
      debounceBuscar(async () => {
        try {
          const todosUsuarios = await loadUsuariosMencao();
          // Filtrar localmente por termo se fornecido
          const lista = (!termo 
            ? todosUsuarios.slice(0, 8)
            : todosUsuarios.filter((u: Usuario) => u.nome.toLowerCase().includes(termo.toLowerCase())).slice(0, 8)
          );
          setSugestoes(lista ?? []);
          setIndiceSelecionado(0);
          setPosicaoDropdown(calcularPosicaoDropdown());
        } catch (e) {
          console.error('[MENCAO] ❌ Erro ao carregar usuários:', e);
          setSugestoes([]);
        } finally {
          setCarregandoSugestoes(false);
        }
      });
      return;
    } catch (error) {
      console.error('[MENCAO] ❌ Erro ao obter sugestões:', error);
      setSugestoes([]);
      setCarregandoSugestoes(false);
    }
  };

           // ✅ CORREÇÃO 3: Calcular posição dinâmica do dropdown
  const calcularPosicaoDropdown = useCallback(() => {
    if (!textareaRef.current || !containerRef.current || !posicaoMencao) {
      return { top: 100, left: 0 };
    }

    const textarea = textareaRef.current;

    // Calcular altura do toolbar usando ref (mais confiável)
    const toolbarHeight = (toolbarRef.current?.offsetHeight ?? 36) + 8; // 8px = mb-2

    // Calcular posição do cursor no textarea
    const cursorPos = textarea.selectionStart;
    const textUntilCursor = textarea.value.slice(0, cursorPos);
    const lines = textUntilCursor.split('\n');
    const cursorLine = lines.length - 1;

    // Calcular altura da linha
    const lhRaw = parseFloat(getComputedStyle(textarea).lineHeight);
    const lineHeight = Number.isFinite(lhRaw) && lhRaw > 1 ? lhRaw : 20;

    // Posição: toolbar + posição da linha do cursor + altura da linha
    const topPosition = toolbarHeight + cursorLine * lineHeight + lineHeight + 4;

    return { top: topPosition, left: 0 };
  }, [posicaoMencao]);

  // Inserir menção selecionada
  const inserirMencao = useCallback(async (usuario: Usuario) => {
    if (!posicaoMencao || !textareaRef.current) return;

    const textarea = textareaRef.current;
    const cursorPos = textarea.selectionStart;
    const { inserirMencaoNoTexto } = await mentions();
    const { novoTexto, novaPosicaoCursor } = inserirMencaoNoTexto(
      effectiveValue,
      cursorPos,
      posicaoMencao,
      usuario
    );

    setEffectiveValue(novoTexto);
    setMostrarSugestoes(false);
    setPosicaoMencao(null);
    setSugestoes([]);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(novaPosicaoCursor, novaPosicaoCursor);
    }, 0);
  }, [effectiveValue, posicaoMencao, setEffectiveValue]);

  // Auto-formatação no Enter + Menções + Envio por teclado
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enviar com Enter (mobile) quando submitOnEnter=true, sem menu de sugestão aberto
    if (!disabled && submitOnEnter && e.key === 'Enter' && !e.shiftKey && !mostrarSugestoes) {
      e.preventDefault();
      const normalizedText = normalizeAllToGFM(effectiveValue);
      if (onSubmitWithPayload) {
        (async () => {
          const { extrairMencoesParaRenderizacao } = await mentions();
          const partes = extrairMencoesParaRenderizacao(normalizedText) || [];
          const mentionsArray = partes.filter((p: any) => p.tipo === 'mencao').map((p: any) => p.conteudo);
          onSubmitWithPayload({ text: normalizedText, mentions: mentionsArray, files: pendingFiles });
          // Limpar rascunho após envio bem-sucedido
          if (enableLocalSave && draftKey) {
            clearDraft();
          }
        })();
      } else {
        setEffectiveValue(normalizedText);
        onSubmit?.();
        // Limpar rascunho após envio bem-sucedido
        if (enableLocalSave && draftKey) {
          clearDraft();
        }
      }
      return;
    }
    
    // Enviar com Ctrl/Cmd+Enter (desktop)
    if (!disabled && submitOnCtrlEnter && e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !mostrarSugestoes) {
      e.preventDefault();
      const normalizedText = normalizeAllToGFM(effectiveValue);
      if (onSubmitWithPayload) {
        (async () => {
          const { extrairMencoesParaRenderizacao } = await mentions();
          const partes = extrairMencoesParaRenderizacao(normalizedText) || [];
          const mentionsArray = partes.filter((p: any) => p.tipo === 'mencao').map((p: any) => p.conteudo);
          onSubmitWithPayload({ text: normalizedText, mentions: mentionsArray, files: pendingFiles });
          // Limpar rascunho após envio bem-sucedido
          if (enableLocalSave && draftKey) {
            clearDraft();
          }
        })();
      } else {
        setEffectiveValue(normalizedText);
        onSubmit?.();
        // Limpar rascunho após envio bem-sucedido
        if (enableLocalSave && draftKey) {
          clearDraft();
        }
      }
      return;
    }
    
    // --- Menções: navegação e seleção ---
    if (mostrarSugestoes && sugestoes.length > 0) {
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setIndiceSelecionado(prev => prev < sugestoes.length - 1 ? prev + 1 : 0);
          return;
        case 'ArrowUp':
          e.preventDefault();
          setIndiceSelecionado(prev => prev > 0 ? prev - 1 : sugestoes.length - 1);
          return;
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          if (sugestoes[indiceSelecionado]) {
            inserirMencao(sugestoes[indiceSelecionado]);
          }
          return;
        case 'Escape':
          e.preventDefault();
          setMostrarSugestoes(false);
          setPosicaoMencao(null);
          setSugestoes([]);
          return;
      }
    }
    
    // --- Formatação avançada (só quando não enviando) ---
    if (e.key !== 'Enter' || disabled) return;
    
    // Se submitOnEnter está ativo, não fazer formatação automática
    if (submitOnEnter) return;
    
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const { selectionStart, value: textValue } = textarea;
    const lines = textValue.substring(0, selectionStart).split('\n');
    const currentLine = lines[lines.length - 1];
    
    // Detectar padrões de lista/bullet/task
    const numeroMatch = currentLine.match(/^(\s*)(\d+)\.\s(.*)$/);
    const bulletMatch = currentLine.match(/^(\s*)●\s(.*)$/);
    const checkboxMatch = currentLine.match(/^(\s*)[◯✓]\s(.*)$/); // ◯ e ✓

    let insertion = '';
    let shouldPrevent = false;
    let removeCurrentPrefix = false; // Para sair da lista em item vazio

    if (numeroMatch) {
      const [, indent, num, content] = numeroMatch;
      if (content.trim() === '') {
        shouldPrevent = true;
        removeCurrentPrefix = true;
      } else {
        const nextNum = parseInt(num) + 1;
        insertion = `\n${indent}${nextNum}. `;
        shouldPrevent = true;
      }
    } else if (bulletMatch) {
      const [, indent, content] = bulletMatch;
      if (content.trim() === '') {
        shouldPrevent = true;
        removeCurrentPrefix = true;
      } else {
        insertion = `\n${indent}● `;
        shouldPrevent = true;
      }
    } else if (checkboxMatch) {
      const [, indent, content] = checkboxMatch;
      if (content.trim() === '') {
        shouldPrevent = true;
        removeCurrentPrefix = true;
      } else {
        insertion = `\n${indent}◯ `;
        shouldPrevent = true;
      }
    }

    if (shouldPrevent) {
      e.preventDefault();

      if (removeCurrentPrefix) {
        // Remove o prefixo vazio e sai da lista
        const lineStart = selectionStart - currentLine.length;
        const newValue = textValue.substring(0, lineStart) + textValue.substring(selectionStart);
        setEffectiveValue(newValue);

        setTimeout(() => {
          if (textarea) {
            textarea.focus();
            textarea.setSelectionRange(lineStart, lineStart);
          }
        }, 0);
      } else {
        // Continuação normal: insere prefixo na próxima linha
        const newValue = textValue.substring(0, selectionStart) + insertion + textValue.substring(selectionStart);
        setEffectiveValue(newValue);

        setTimeout(() => {
          if (textarea) {
            const newPos = selectionStart + insertion.length;
            textarea.focus();
            textarea.setSelectionRange(newPos, newPos);
          }
        }, 0);
      }
    }
    // Após Enter, mantém caret visível no mobile
    setTimeout(() => onEnsureVisible?.(textareaRef.current!), 0);
  };

  // ✅ CORREÇÃO 4: Função para converter símbolos ao digitar + menções COM LOGS
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (disabled) return;
    
    let newValue = e.target.value;
    // Converter símbolos para versões visuais
    // IMPORTANTE: task items (- [x], - [ ]) ANTES de bullets (- ) para não consumir o prefixo
    newValue = newValue
      .replace(/^(\s*)-\s\[[xX]\]\s/gm, '$1✓ ')   // - [x] → ✓ (checked task)
      .replace(/^(\s*)-\s\[\s*\]\s/gm, '$1◯ ')     // - [ ] → ◯ (unchecked task)
      .replace(/^(\s*)- /gm, '$1● ')                // -     → ● (bullet)
      .replace(/^(\s*)•\s/gm, '$1● ')               // •     → ● (small to large)
      .replace(/^(\s*)○\s/gm, '$1◯ ');              // ○     → ◯ (small to large)
    setEffectiveValue(newValue);

    // --- Scroll automático para acompanhar digitação ---
    setTimeout(() => {
      if (textareaRef.current) {
        const textarea = textareaRef.current;
        // Calcula a posição do cursor
        const cursorPos = textarea.selectionStart;
        // Pega o texto até o cursor
        const textUntilCursor = textarea.value.slice(0, cursorPos);
        // Conta o número de quebras de linha até o cursor
        const lines = textUntilCursor.split('\n');
        // Calcula a linha do cursor
        const cursorLine = lines.length - 1;
        // Calcula a altura da linha
        const lhRaw = parseFloat(getComputedStyle(textarea).lineHeight);
        const lineHeight = Number.isFinite(lhRaw) && lhRaw > 1 ? lhRaw : 20;
        // Calcula a posição Y do cursor
        const cursorY = cursorLine * lineHeight;
        // Se o cursor está fora da área visível, ajusta o scroll
        if (cursorY < textarea.scrollTop) {
          textarea.scrollTop = cursorY;
        } else if (cursorY + lineHeight > textarea.scrollTop + textarea.clientHeight) {
          textarea.scrollTop = cursorY + lineHeight - textarea.clientHeight;
        }
      }
    }, 0);

    // --- Menções: sempre detecta; a busca decide entre local/remoto ---
    const textarea = textareaRef.current;
    if (textarea) {
      const cursorPos = textarea.selectionStart;
      (async () => {
        try {
          const { detectarMencaoEmAndamento } = await mentions();
          const mencao = detectarMencaoEmAndamento(newValue, cursorPos);
          if (mencao) {
            setPosicaoMencao(mencao);
            setMostrarSugestoes(true);
            buscarSugestoes(mencao.termo);
          } else {
            setMostrarSugestoes(false);
            setPosicaoMencao(null);
            setSugestoes([]);
          }
        } catch (error) {
          console.error('[MENCAO] ❌ Erro na detecção de menção:', error);
          setMostrarSugestoes(false);
          setPosicaoMencao(null);
          setSugestoes([]);
        }
      })();
    }

    // 🔧 Sempre que o texto mudar, garante visibilidade acima do teclado no mobile
    setTimeout(() => onEnsureVisible?.(textareaRef.current!), 0);
  };

  // Toggle checkbox (◯ ↔ ✓)
  const toggleCheckboxAtCursor = () => {
    if (disabled) return;
    
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { selectionStart } = textarea;
    const lineStart = effectiveValue.lastIndexOf('\n', selectionStart - 1) + 1;
    const lineEnd = effectiveValue.indexOf('\n', selectionStart);
    const currentLineEnd = lineEnd === -1 ? effectiveValue.length : lineEnd;
    const currentLineText = effectiveValue.substring(lineStart, currentLineEnd);

    // Verificar se é uma linha de checkbox - usando símbolos maiores
    const pendingMatch = currentLineText.match(/^(\s*)◯\s(.*)$/);
    const completeMatch = currentLineText.match(/^(\s*)✓\s(.*)$/);

    if (pendingMatch) {
      // Mudar ◯ para ✓
      const [, indent, content] = pendingMatch;
      const newLine = `${indent}✓ ${content}`;
      const newValue = effectiveValue.substring(0, lineStart) + newLine + effectiveValue.substring(currentLineEnd);
      setEffectiveValue(newValue);
      
      setTimeout(() => {
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(selectionStart, selectionStart);
        }
      }, 0);
    } else if (completeMatch) {
      // Mudar ✓ para ◯
      const [, indent, content] = completeMatch;
      const newLine = `${indent}◯ ${content}`;
      const newValue = effectiveValue.substring(0, lineStart) + newLine + effectiveValue.substring(currentLineEnd);
      setEffectiveValue(newValue);
      
      setTimeout(() => {
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(selectionStart, selectionStart);
        }
      }, 0);
    }
  };

  // Handle double click para toggle checkboxes - com área maior de clique
  const handleDoubleClick = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    if (disabled) return;
    
    const textarea = e.target as HTMLTextAreaElement;
    const { selectionStart } = textarea;
    const lineStart = effectiveValue.lastIndexOf('\n', selectionStart - 1) + 1;
    const lineEnd = effectiveValue.indexOf('\n', selectionStart);
    const currentLineEnd = lineEnd === -1 ? effectiveValue.length : lineEnd;
    const currentLineText = effectiveValue.substring(lineStart, currentLineEnd);

    // Se double click em linha de checkbox, fazer toggle - símbolos maiores
    if (currentLineText.match(/^(\s*)[◯✓]\s/)) {
      e.preventDefault();
      toggleCheckboxAtCursor();
    }
  };

  // Handle mouse move para cursor pointer nos checkboxes
  const handleMouseMove = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    if (disabled) return;
    
    const textarea = e.target as HTMLTextAreaElement;
    const rect = textarea.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
         // Calcular posição aproximada do cursor no texto
     const scrollTop = textarea.scrollTop;
     const scrollLeft = textarea.scrollLeft;
     const lhRaw = parseFloat(getComputedStyle(textarea).lineHeight);
     const fsRaw = parseFloat(getComputedStyle(textarea).fontSize);
     const lineHeight = Number.isFinite(lhRaw) && lhRaw > 1 ? lhRaw : 20;
     const fontSize = Number.isFinite(fsRaw) && fsRaw > 1 ? fsRaw : 14;
     const padding = 12; // px-3 = 12px
    
    const adjustedY = y + scrollTop - padding;
    const adjustedX = x + scrollLeft - padding;
    const lineIndex = Math.floor(adjustedY / lineHeight);
    const charWidth = fontSize * 0.6; // Aproximação para caracteres monospace
    const charIndex = Math.floor(adjustedX / charWidth);
    
    const lines = effectiveValue.split('\n');
    if (lineIndex >= 0 && lineIndex < lines.length) {
      const line = lines[lineIndex];
      const isCheckboxLine = line.match(/^(\s*)[◯✓]\s/);
             const isOverCheckbox = isCheckboxLine && charIndex <= 4;
      
      textarea.style.cursor = isOverCheckbox ? 'pointer' : 'text';
    } else {
      textarea.style.cursor = 'text';
    }
  };

  // Handle single click para melhor UX dos checkboxes
  const handleClick = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    if (disabled) return;
    
    const textarea = e.target as HTMLTextAreaElement;
    const { selectionStart } = textarea;
    const lineStart = effectiveValue.lastIndexOf('\n', selectionStart - 1) + 1;
    const lineEnd = effectiveValue.indexOf('\n', selectionStart);
    const currentLineEnd = lineEnd === -1 ? effectiveValue.length : lineEnd;
    const currentLineText = effectiveValue.substring(lineStart, currentLineEnd);

    // Clique próximo ao início da linha (até 4 chars) conta como clique no checkbox
    const relativeCursorPos = selectionStart - lineStart;
    if (relativeCursorPos <= 4 && currentLineText.match(/^(\s*)[◯✓]\s/)) {
      setTimeout(() => toggleCheckboxAtCursor(), 100); // Pequeno delay para melhor UX
    }
  };

  // Inserir formatação com símbolos maiores
  const inserirFormatacao = (tipo: 'numeracao' | 'bullet' | 'checkbox') => {
    if (disabled) return;
    
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { selectionStart, selectionEnd, value: textValue } = textarea;
    const selectedText = textValue.substring(selectionStart, selectionEnd);
    
    if (selectedText) {
      const lines = selectedText.split('\n');
      const formattedLines = lines.map((line, index) => {
        if (!line.trim()) return line;
        
        // Remover formatação existente - incluindo símbolos maiores E padrão GFM de tasks (- [ ] / - [x])
        // Assim evitamos duplicar prefixo e não "des-ticar" a linha ao reformatar.
        const cleanLine = line
          // números, bullets e símbolos (• ● ◯ ✓)
          .replace(/^(\s*)((\d+\.)|([•●◯✓]))\s*/, '$1')
          // padrão GFM: "- [ ] " ou "- [x] " (maiúsc/minúsc)
          .replace(/^(\s*)-\s\[(?:\s|x|X)\]\s*/, '$1')
          .trim();
        if (!cleanLine) return line;
        
        switch (tipo) {
          case 'numeracao':
            return `${index + 1}. ${cleanLine}`;
          case 'bullet':
            return `● ${cleanLine}`;
          case 'checkbox':
            return `◯ ${cleanLine}`;
          default:
            return line;
        }
      });
      
      const newText = textValue.substring(0, selectionStart) + formattedLines.join('\n') + textValue.substring(selectionEnd);
      setEffectiveValue(newText);
      
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(selectionStart, selectionStart + formattedLines.join('\n').length);
        // 🔧 após aplicar formatação, mantém caret visível
        onEnsureVisible?.(textareaRef.current!);
      }, 0);
    } else {
      const lineStart = textValue.lastIndexOf('\n', selectionStart - 1) + 1;
      const lineEnd = textValue.indexOf('\n', selectionStart);
      const currentLineEnd = lineEnd === -1 ? textValue.length : lineEnd;
      const currentLine = textValue.substring(lineStart, currentLineEnd);
      
      let insertion = '';
      let replaceCurrentLine = false;
      
      if (currentLine.trim() === '') {
        replaceCurrentLine = true;
        switch (tipo) {
          case 'numeracao':
            insertion = '1. ';
            break;
          case 'bullet':
            insertion = '● ';
            break;
          case 'checkbox':
            insertion = '◯ ';
            break;
        }
      } else {
        switch (tipo) {
          case 'numeracao':
            insertion = selectionStart === 0 || textValue[selectionStart - 1] === '\n' ? '1. ' : '\n1. ';
            break;
          case 'bullet':
            insertion = selectionStart === 0 || textValue[selectionStart - 1] === '\n' ? '● ' : '\n● ';
            break;
          case 'checkbox':
            insertion = selectionStart === 0 || textValue[selectionStart - 1] === '\n' ? '◯ ' : '\n◯ ';
            break;
        }
      }

      let newText;
      let newCursorPos;
      
      if (replaceCurrentLine) {
        newText = textValue.substring(0, lineStart) + insertion + textValue.substring(currentLineEnd);
        newCursorPos = lineStart + insertion.length;
      } else {
        newText = textValue.substring(0, selectionStart) + insertion + textValue.substring(selectionStart);
        newCursorPos = selectionStart + insertion.length;
      }

      setEffectiveValue(newText);
      
      setTimeout(() => {
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }
  };

  // Efeito para auto-scroll das sugestões
  useEffect(() => {
    if (mostrarSugestoes && sugestoesRef.current && sugestoes.length > 0) {
      const el = sugestoesRef.current.children[indiceSelecionado + 1] as HTMLElement; // +1 para pular o header
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [indiceSelecionado, mostrarSugestoes, sugestoes.length]);

  // ✅ Recalcular posição quando mostrar sugestões
  useEffect(() => {
    if (mostrarSugestoes) {
      const novaPos = calcularPosicaoDropdown();
      setPosicaoDropdown(novaPos);
    }
  }, [calcularPosicaoDropdown, mostrarSugestoes, posicaoMencao]);

  // ✅ FUNÇÃO PRINCIPAL: Ajustar altura do textarea dinamicamente em tempo real
  const ajustarAltura = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Resetar altura para calcular corretamente
    textarea.style.height = 'auto';
    
    // Calcular altura baseada no conteúdo real
    const scrollHeight = textarea.scrollHeight;
    const lhRaw = parseFloat(getComputedStyle(textarea).lineHeight);
    const lineHeight = Number.isFinite(lhRaw) && lhRaw > 1 ? lhRaw : 20;
    
    // Definir altura mínima e máxima
    const minHeight = lineHeight * minRows;
    const maxHeight = lineHeight * maxRows;
    
    // Aplicar altura calculada (usar scrollHeight para altura real do conteúdo)
    const newHeight = Math.max(minHeight, Math.min(maxHeight, scrollHeight));
    textarea.style.height = `${newHeight}px`;
    
    // ✅ NOVO: Verificar se precisa de scroll com margem de tolerância
    const precisaScroll = scrollHeight > (maxHeight + 5); // +5px de tolerância
    if (precisaScroll) {
      textarea.style.overflowY = 'auto';
      // ✅ NOVO: Aplicar estilos de scroll discreto com transição
      textarea.classList.add('has-scroll');
    } else {
      textarea.style.overflowY = 'hidden';
      textarea.classList.remove('has-scroll');
    }
  }, [maxRows, minRows]);

  // ✅ EFEITO: Ajustar altura quando o valor mudar (fallback para mudanças externas)
  useEffect(() => {
    ajustarAltura();
  }, [ajustarAltura, maxRows, minRows, safeValue]);


  // ✅ EFEITO: Recalcular posição do dropdown quando altura mudar
  useEffect(() => {
    if (mostrarSugestoes) {
      const novaPos = calcularPosicaoDropdown();
      setPosicaoDropdown(novaPos);
    }
  }, [calcularPosicaoDropdown, mostrarSugestoes, safeValue]); // Recalcular quando o valor mudar (altura pode ter mudado)


  // Preload mentions module para evitar overhead async em cada keystroke
  useEffect(() => { mentions(); }, []);

  // ✅ NOVO: Carregar rascunho quando salvamento local estiver habilitado
  useEffect(() => {
    if (enableLocalSave && draftKey) {
      loadDraft(); // restaura rascunho anterior, se existir
    }
  }, [enableLocalSave, draftKey, loadDraft]);

  // ✅ NOVO: Sincronizar valor externo com rascunho quando não houver rascunho salvo
  useEffect(() => {
    if (enableLocalSave && draftKey && safeValue && !draftValue.text) {
      setDraftValue({ text: safeValue });
    }
  }, [enableLocalSave, draftKey, safeValue, draftValue.text, setDraftValue]);

  // Gerenciar URLs de pré-visualização dos anexos pendentes
  const previewUrlsRef = useRef<Record<string, string>>({});

  useEffect(() => {
    previewUrlsRef.current = previewUrls;
  }, [previewUrls]);

  useEffect(() => {
    setPreviewUrls((prev) => {
      const keep = new Set(normalizedPendingFiles.map(fileSig));
      const next: Record<string, string> = {};
      let changed = false;

      for (const f of normalizedPendingFiles) {
        const key = fileSig(f);
        const existing = prev[key];
        if (existing) {
          next[key] = existing;
        } else {
          next[key] = URL.createObjectURL(f);
          changed = true;
        }
      }

      for (const key of Object.keys(prev)) {
        if (!keep.has(key)) {
          URL.revokeObjectURL(prev[key]);
          changed = true;
        }
      }

      if (!changed && Object.keys(prev).length === Object.keys(next).length) {
        return prev;
      }

      return next;
    });
  }, [normalizedPendingFiles]);

  useEffect(() => {
    return () => {
      const urls = previewUrlsRef.current;
      Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`${className} ${dragOver ? 'ring-2 ring-blue-400 ring-offset-2 ring-offset-transparent' : ''}`}
      style={{ position: 'relative' }}
      onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        // @ts-ignore
        e.nativeEvent?.stopImmediatePropagation?.();
        setDragOver(false);
        const files = Array.from(e.dataTransfer?.files || []);
        const unicos = uniqueNewFiles(files);
        if (unicos.length) onPasteFiles?.(unicos);
      }}
    >
             {/* Toolbar otimizada */}
       {showToolbar && (
       <div ref={toolbarRef} className="flex items-center justify-between gap-1 sm:gap-2 mb-2">
        <div className="flex items-center gap-1 sm:gap-2">
          <button
            type="button"
            onClick={() => inserirFormatacao('numeracao')}
            disabled={disabled}
            className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm border border-secondary dark:border-gray-600 rounded hover:bg-secondary/10 dark:hover:bg-gray-700/50 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 dark:text-white"
            title="Adicionar numeração (1, 2, 3...)"
          >
            <Type className="w-3 h-3 text-gray-600 dark:text-gray-300" />
            <span className="hidden sm:inline">1,2,3</span>
            <span className="sm:hidden">123</span>
          </button>
          
          <button
            type="button"
            onClick={() => inserirFormatacao('bullet')}
            disabled={disabled}
            className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm border border-secondary dark:border-gray-600 rounded hover:bg-secondary/10 dark:hover:bg-gray-700/50 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 dark:text-white"
            title="Adicionar bullets (●)"
          >
            <List className="w-3 h-3 text-gray-600 dark:text-gray-300" />
            <span className="text-xs sm:text-sm">Bullets</span>
          </button>
          
          <button
            type="button"
            onClick={() => inserirFormatacao('checkbox')}
            disabled={disabled}
            className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm border border-secondary dark:border-gray-600 rounded hover:bg-secondary/10 dark:hover:bg-gray-700/50 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 dark:text-white"
            title="Adicionar tasks (◯/✓) - Clique duplo para marcar/desmarcar"
          >
            <CheckSquare className="w-3 h-3 text-gray-600 dark:text-gray-300" />
            <span className="text-xs sm:text-sm">Tasks</span>
          </button>
        </div>

        {/* Ações à direita: Anexar + Enviar */}
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Botão Anexar (ao lado esquerdo do Enviar) */}
          <button
            type="button"
            onClick={handleAttachClick}
            disabled={disabled || sending}
            className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm border border-secondary dark:border-gray-600 rounded hover:bg-secondary/10 dark:hover:bg-gray-700/50 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 dark:text-white"
            title="Anexar arquivos"
            aria-label="Anexar arquivos"
          >
            <Paperclip className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            <span className="hidden sm:inline">Anexar</span>
          </button>
          <input
            key={fileInputKey}
            ref={fileInputRef}
            type="file"
            accept={acceptStr}
            multiple
            className="hidden"
            onClick={(e) => {
              // ajuda iOS/Android a disparar change mesmo escolhendo o mesmo arquivo
              (e.currentTarget as HTMLInputElement).value = '';
            }}
            onMouseDown={(e) => { e.stopPropagation(); }}
            onTouchStart={(e) => { e.stopPropagation(); }}
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              dbg('[InputChange]', files);
              const unicos = uniqueNewFiles(files);
              if (unicos.length) onPasteFiles?.(unicos);
              // força remount pra garantir novo 'change' depois
              setFileInputKey(k => k + 1);
            }}
          />

          {/* Botão Enviar */}
          {(onSubmit || onSubmitWithPayload) && (
            <button
              type="button"
                             onClick={async () => {
                 if (sending) return;
                 if (onSubmitWithPayload) {
                   const normalizedText = normalizeAllToGFM(effectiveValue);
                   const { extrairMencoesParaRenderizacao } = await mentions();
                   const partes = extrairMencoesParaRenderizacao(normalizedText) || [];
                   const mentionsArray = partes.filter((p: any) => p.tipo === 'mencao').map((p: any) => p.conteudo);
                   onSubmitWithPayload({ text: normalizedText, mentions: mentionsArray, files: pendingFiles });
                   // Limpar rascunho após envio bem-sucedido
                   if (enableLocalSave && draftKey) {
                     clearDraft();
                   }
                 } else {
                   // Normalizar texto antes de enviar mesmo no onSubmit simples
                   const normalizedText = normalizeAllToGFM(effectiveValue);
                   setEffectiveValue(normalizedText); // Atualiza o estado local com texto normalizado
                   onSubmit?.();
                   // Limpar rascunho após envio bem-sucedido
                   if (enableLocalSave && draftKey) {
                     clearDraft();
                   }
                 }
               }}
              disabled={sending || disabled || (!effectiveValue.trim() && (!pendingFiles || pendingFiles.length === 0))}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              aria-label="Enviar comentário"
                             title={submitOnEnter ? 'Enviar (Enter). Shift+Enter: nova linha' : 'Enviar (Ctrl/Cmd+Enter). Enter: nova linha'}
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              <span className="hidden sm:mb-0.5">{sendLabel}</span>
            </button>
          )}
        </div>
      </div>
      )}

             {/* Textarea com cursor inteligente para checkboxes, altura dinâmica em tempo real e scroll discreto */}
      <textarea
        ref={textareaRef}
        value={effectiveValue}
                 onChange={(e) => { handleChange(e); }}
        onKeyDown={handleKeyDown}
        onClick={(e) => { handleClick(e); onEnsureVisible?.(textareaRef.current!); }}
        onDoubleClick={handleDoubleClick}
        onMouseMove={handleMouseMove}
        onFocus={() => onEnsureVisible?.(textareaRef.current!)}
        onKeyUp={() => onEnsureVisible?.(textareaRef.current!)}
        autoFocus={autoFocus}
        onPaste={(e) => {
          if (disabled) return;
          const items = Array.from(e.clipboardData?.items || []);
          const files: File[] = [];
          for (const it of items) {
            if (it.kind === 'file') {
              const f = it.getAsFile();
              if (f && (f.type?.startsWith('image/') || f.type?.startsWith('video/'))) {
                files.push(f);
              }
            }
          }
          if (files.length) {
            // evita colar a imagem inline como data URL no textarea
            e.preventDefault();
            const unicos = uniqueNewFiles(files);
            if (unicos.length) onPasteFiles?.(unicos);
          }
        }}
        disabled={disabled}
        rows={minRows}
        className="editor-textarea w-full px-3 py-2 border border-secondary dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700 text-foreground dark:text-white placeholder-gray-500 dark:placeholder-gray-300 focus:ring-2 focus:ring-primary dark:focus:ring-blue-400 focus:border-primary dark:focus:border-blue-400 font-ageo-regular resize-none transition-all duration-200 leading-relaxed disabled:opacity-50 disabled:cursor-not-allowed"
        placeholder={placeholder}
        style={{ 
          fontSize: '15px',
          lineHeight: '1.7',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}
      />

      

      {dragOver && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="px-3 py-1 text-xs rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
            Solte as imagens aqui…
          </div>
        </div>
      )}
      
      {/* ✅ Dropdown de sugestões de menção com POSIÇÃO DINÂMICA */}
      {mostrarSugestoes && (
        <div
          ref={sugestoesRef}
          className="absolute z-[9999] w-full max-w-xs rounded-lg shadow-lg border py-1 max-h-48 overflow-y-auto bg-background dark:bg-gray-800 border-secondary dark:border-gray-600"
          style={{ 
            top: posicaoDropdown.top, 
            left: posicaoDropdown.left 
          }}
        >
          
          
          {/* Loading */}
          {carregandoSugestoes && (
            <div className="px-3 py-2 text-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary dark:border-blue-400 mx-auto"></div>
            </div>
          )}
          
          {/* ✅ DEBUG: Mostrar termo sendo buscado */}
          {!carregandoSugestoes && posicaoMencao && (
            <div className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
              Buscando: "{posicaoMencao.termo || '(todos)'}" 
            </div>
          )}
          
          {/* Sugestões */}
          {!carregandoSugestoes && sugestoes.length > 0 && (
            sugestoes.map((usuario, index) => (
              <button
                key={usuario.id}
                onClick={() => inserirMencao(usuario)}
                className={`w-full text-left px-3 py-2 flex items-center gap-2 transition-colors ${
                  index === indiceSelecionado
                    ? 'bg-primary/10 text-primary dark:bg-blue-900/30 dark:text-blue-400'
                    : 'hover:bg-secondary/20 text-gray-700 dark:hover:bg-gray-700 dark:text-gray-200'
                }`}
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  index === indiceSelecionado
                    ? 'bg-primary text-white'
                    : 'bg-secondary text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {usuario.nome.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-ageo-regular truncate text-gray-700 dark:text-gray-200">{usuario.nome}</div>
                  <div className="text-xs truncate text-gray-500 dark:text-gray-400">{usuario.email}</div>
                </div>
              </button>
            ))
          )}
          
          {/* Nenhum resultado */}
          {!carregandoSugestoes && sugestoes.length === 0 && posicaoMencao && (
            <div className="px-3 py-2 text-center text-sm text-gray-500 dark:text-gray-400">
              <User className="w-4 h-4 mx-auto mb-1 text-gray-500 dark:text-gray-400" /> 
            </div>
          )}
          
          {/* Dica de uso */}
          <div className="px-3 py-1 text-xs border-t text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700">
            ↑↓ navegar • Enter/Tab selecionar • Esc fechar
          </div>
        </div>
      )}

      {/* Pré-visualização de anexos pendentes (opcional) */}
      {showPendingPreview && pendingFiles?.length > 0 && (
        <div className="mt-2 space-y-2">
          <div className="text-sm font-ageo-bold text-gray-700 dark:text-gray-100">
            📎 Anexos ({pendingFiles.length})
          </div>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
            {pendingFiles.map((file) => {
              const key = `${file.name}|${file.size}|${(file as any).lastModified ?? 0}`;
              const url = previewUrls[key];
              const isImg = file.type?.startsWith('image/');
              const isVid = file.type?.startsWith('video/');
              const isPdf = file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf');
              return (
                <div key={key} className="relative group border border-secondary dark:border-gray-600 rounded overflow-hidden">
                  {isImg && url && <img src={url} alt={file.name} className="w-full h-16 object-cover" />}
                  {isVid && <div className="w-full h-16 bg-secondary/30 dark:bg-gray-700 flex items-center justify-center text-xs">Vídeo</div>}
                  {isPdf && (url ? (
                    <PdfThumb src={file} className="w-full h-16 object-cover bg-white" />
                  ) : (
                    <div className="w-full h-16 bg-secondary/30 dark:bg-gray-800 flex items-center justify-center text-[11px] font-medium">PDF</div>
                  ))}
                  {!isImg && !isVid && !isPdf && <div className="w-full h-16 bg-secondary/30 dark:bg-gray-700 flex items-center justify-center text-xs px-1 text-center">{file.name}</div>}
                  {!!onRemovePendingFile && (
                    <button
                      type="button"
                      onClick={() => onRemovePendingFile(file)}
                      className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                      title="Remover"
                    >×</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

